import { Industry } from "@/types/survey";

export const en = {
  common: {
    submit: "Submit",
    next: "Next",
    back: "Back",
    loading: "Loading...",
    error: "Error",
    success: "Success",
    yes: "Yes",
    no: "No",
    login: "Sign In"
  },
  nav: {
    startOver: "Start Over",
    backToHome: "Back to Home",
    previous: "Previous",
    next: "Next",
    seeResults: "See Results",
    of: "of",
    dashboard: "Dashboard"
  },
  index: {
    title: "Lovable Projects Assessment",
    description:
      "Utilize BEAM's proprietary diagnostic platform to assess Net Emotional Value (NEV) and determine Emotional Return on Investment (EROI) for your project or organization.",
    startButton: "Start Assessment",
    aboutLink: "Learn More",
    heroTitle: "Unlock the Power of Emotional Connection",
    heroSubtitle:
      "BEAMEvaluate Lite - Net Emotional Value (NEV)/Emotional Rate of Return (EROI) Evaluation Tool for your project or business.",
    keyBenefitsTitle: "Key Benefits",
    keyBenefit1: "Understand your project's emotional impact",
    keyBenefit2: "Identify areas for improvement",
    keyBenefit3: "Benchmark against industry leaders",
    keyBenefit1Description: "Discover how your brand resonates emotionally with your audience and where it falls on the NEV scale.",
    keyBenefit2Description: "Get data-driven insights on specific areas where your brand can establish stronger emotional connections.",
    keyBenefit3Description: "Compare your emotional performance metrics with industry standards and leading competitors.",
    testimonialsTitle: "What Our Clients Say",
    testimonial1:
      "This assessment gave us actionable insights to strengthen our customer relationships.",
    testimonial2:
      "We saw a significant increase in customer loyalty after implementing the recommended changes.",
    footer: "© 2025 BaaBaa Emotive Asset Management Group. All rights reserved.",
    whyBeamevaluate: "Why BEAMEvaluate",
    whyDescription: "Startups face a unique challenge: balancing rapid growth with emotional and operational resilience. Traditional metrics like NPS, CSAT, and financial KPIs often fail to capture the emotional dynamics and ethical alignment critical for long-term success. BEAM Evaluate LITE bridges this gap by offering a lightweight, scalable, and scientifically validated framework for assessing startup health.",
    beyondGrowthMetrics: "Beyond Growth Metrics",
    beyondGrowthDesc: "Measures emotional connection and sustainability beyond traditional growth KPIs",
    emotionalIntelligence: "Emotional Intelligence",
    emotionalIntelligenceDesc: "Captures the qualitative emotional dynamics critical for long-term stakeholder relationships",
    scientificValidation: "Scientific Validation",
    scientificValidationDesc: "Utilizes scientifically validated frameworks to quantify emotional and ethical metrics",
    holisticAssessment: "Holistic Assessment",
    holisticAssessmentDesc: "Evaluates both immediate emotional impact and long-term sustainability factors",
    whyBeamevaluateButton: "Why BEAMEvaluate",
    methodologyButton: "BEAM's Methodologies",
    tagline: "Emotion-Driven Analytics",
    learnMoreMethodology: "Learn more about our methodology",
    ctaTitle: "Ready to discover your BEAM Score?",
    ctaDescription: "Start your evaluation today and gain valuable insights into your business's emotional and strategic metrics.",
    startEvaluation: "Start Free Evaluation",
    footerDescription: "BEAM Evaluate provides business leaders with emotional and strategic metrics to make better decisions."
  },
  industrySelector: {
    title: "Select Your Industry",
    description: "Choose the industry that best represents your business.",
    selectIndustry: "Select an industry",
  },
  survey: {
    title: "Project Assessment Survey",
    description: "Answer the following questions to assess your project's NEV.",
    question: "Question",
    required: "Required",
    tooltip: "More Info",
    explanation: "Explanation",
    example: "Example",
    wordCount: "Word Count",
    manual: "Manual Survey",
    chat: {
      title: "BEAM AI Survey"
    },
    ai: {
      guide: "AI Methodology Guide",
      applied: "AI suggestions applied",
      description: "The AI has analyzed your business and applied suggested answers",
      methodology: {
        description: "The AI Methodology Guide helps you understand the survey's structure and how to provide meaningful answers."
      },
      chat: {
        welcome: "I am BEAM's AI Agent - BEAMER. Welcome to our survey. I'll be asking you questions one by one. Let's get started!",
        complete: "Thank you for completing the survey!"
      }
    },
    data: {
      local: "Your data is stored locally in your browser."
    },
    platform: {
      info: "This platform is powered by BaaBaa Emotive Asset Management Group."
    },
    answer: {
      required: "Answer Required"
    },
    more: {
      detail: "Please provide more detail to proceed."
    },
    likert: {
      1: "Very low",
      2: "Low",
      3: "Neutral",
      4: "High",
      5: "Very high"
    },
    rating: "Rating",
    dropdown: {
      placeholder: "Select an option"
    },
    help: "Help",
    unsupported: "Unsupported question type",
    text: {
      explanation: "Explanation",
      current: "Current words",
      required: "Required",
      instructions: "Minimum words required",
      placeholder: "Type your response here...",
      minWords: "Please provide at least 5 words for a meaningful response.",
      example: "Example"
    },
    project: {
      description: "Project Description"
    },
    generate: {
      guidance: "Generate Guidance"
    }
  },
  results: {
    title: "BEAMEvaluate - Assessment Report",
    description: "Here's a breakdown of your project's NEV and key metrics.",
    nevScore: "Net Emotional Value (NEV) Score",
    rating: "Rating",
    eroi: "Emotional Return on Investment (EROI)",
    recommendations: "Top Recommendations",
    keyInsight: "Key Insight",
    industryPercentile: "Industry Percentile",
    emotionalEquity: "Emotional Equity",
    legacyQuotient: "Legacy Quotient",
    frictionCoefficient: "Friction Coefficient",
    trustBeta: "Trust Beta",
    behavioralConversion: "Behavioral Conversion",
    community: "Community",
    ethics: "Ethics",
    funding: "Funding",
    innovation: "Innovation",
    benchmarking: "Benchmarking",
    industryAverages: "Industry Averages",
    yourScore: "Your Score",
    topRecommendations: "Top Recommendations",
    noRecommendations: "No specific recommendations available.",
    emotionalHeatmap: "Emotional Heatmap",
    dominantEmotions: "Dominant Emotions",
    sentimentAnalysis: "Sentiment Analysis",
    detectedIssues: "Detected Issues",
    noIssuesDetected: "No specific issues detected.",
    industryBenchmark: "Industry Benchmark",
    competitorBenchmark: "Competitor Benchmark",
    nev: "NEV",
    eroiValue: "EROI Value",
    riskRadar: "Risk Assessment Distribution",
    riskFactors: "Risk Factors",
    riskLevel: "Risk Level",
    low: "Low",
    medium: "Medium",
    high: "High",
    veryHigh: "Very High",
    download: 'Download PDF',
    downloadReport: "Download Report",
    shareResults: "Share Results",
    restartSurvey: "Restart Survey",
    detailedBreakdown: "Detailed Breakdown",
    submetrics: "Submetrics",
    fandom: "Fandom",
    trust: "Trust",
    referrals: "Referrals",
    redFlags: "Red Flags",
    brightSpots: "Bright Spots",
    projection: "Projection",
    back: "Back to Survey",
    generating: "Generating",
    exporting: "Exporting",
    saving: "Saving",
    pdf: "Export PDF",
    csv: "Export CSV",
    save: "Save Results",
    saved: "Results Saved",
    savedDesc: "Your assessment results have been saved to your account.",
    saveError: "Save Error",
    saveErrorDesc: "There was a problem saving your results.",
    standard: "Standard View",
    professional: "Professional Dashboard",
    ai: "AI Insights",
    backToDashboard: "Back to Dashboard",
    understandingBeamScore: "**The BEAM Score** measures emotional and strategic value on a scale of 0-10 across diverse asset classes. Higher scores (8-10) indicate exceptional emotional resonance, strong ethical alignment, and high adaptability potential. Lower scores (0-3) suggest immediate improvement areas in stakeholder engagement and strategic positioning.",
    applicationTitle: "**BEAM Score Applications:**",
    industryStandards: "Benchmarks your project against industry standards",
    improvementOpportunities: "Identify emotional and brand value improvement opportunities",
    trackProgress: "Track progress over time as you implement recommendations",
    compareProjects: "Compare different projects across your portfolio",
    measureROI: "Measure return on investment for emotional engagement initiatives",
    noDataDownload: "No data available to download",
    errorDesc: "There was an error retrieving the data",
    categoryDistribution: "Category Distribution",
    metricsBreakdown: "Metrics Breakdown",
    basicView: "Basic View",
    advancedView: "Advanced View",
    beamAIInsights: "BEAM AI Insights",
    beamMethodology: "BEAM Methodology",
    beamEvaluateAnalysis: "BEAMEvaluate Analysis",
    beamEvaluateMetrics: "BEAMEvaluate Metrics",
    backToSurvey: "Back to Survey",
    performanceAnalysis: "Performance Analysis",
    riskAssessment: "Risk Assessment",
    industryBenchmarking: "Industry Benchmarking",
    recommendationHighPriority: "This high-priority recommendation addresses the most critical aspect of your project's emotional value.",
    recommendationMediumPriority: "This recommendation focuses on important aspects that will significantly enhance your project's connection.",
    recommendationLowPriority: "This recommendation provides additional value and should be considered after addressing higher priority items."
  },
  metrics: {
    title: "Understanding the Metrics",
    nev: "Net Emotional Value (NEV)",
    nev_description: "A comprehensive 0-50 score that measures the holistic emotional and strategic value of a project. NEV integrates multiple dimensions including emotional attachment, ethical legacy, trust, and long-term potential.",
    eroi: "Emotional Return on Investment (EROI)",
    eroi_description: "A multiplier that quantifies how effectively emotional investment translates into tangible value creation across your asset or project. This metric helps predict future performance based on current emotional engagement levels.",
    high: "High Risk (0-3.3)",
    high_description: "Your project faces significant challenges in building emotional connections and may struggle to maintain customer loyalty.",
    competitive: "Competitive (3.3-6.6)",
    competitive_description: "Your project is performing at industry standard, with moderate emotional connection and average customer retention.",
    exceptional: "Exceptional (6.6-10)",
    exceptional_description: "Your project creates powerful emotional connections, driving strong loyalty and substantial business growth.",
    assessment: "This assessment is based on proprietary algorithms developed by BaaBaa Emotive Asset Management Group",
    emotional: "Emotional Attachment",
    legacy: "Legacy Value",
    friction: "Friction",
    trust: "Trust",
    continuity: "Continuity",
    beamScore: "BEAM Score",
    beamScore_description: "A refined 0-10 index that condenses complex emotional and strategic metrics into a single, actionable performance indicator. The score synthesizes emotional intelligence, ethical alignment, innovation potential, and stakeholder resonance.",
  },
  app: {
    copyright: "© 2025 BaaBaa Emotive Asset Management Group. All rights reserved.",
    start: "Start Assessment",
    methodology: "Methodology",
    beamMethodology: "BEAM's Methodology",
    tagline: "Utilize BEAM's proprietary diagnostic platform to assess Net Emotional Value (NEV) and determine Emotional Return on Investment (EROI) for your project or organization."
  },
  methodology: {
    title: "Assessment Methodology",
    subtitle: "Learn how we evaluate your project's emotional connection",
    description: "BEAM's methodology is based on scientific research in emotional marketing and brand attachment theories. We analyze how your project creates meaningful connections with users through a series of targeted questions.",
    ea: {
      title: "Emotional Attachment (EA)",
      description: "Measures the strength of user emotional connection, project loyalty, and community engagement",
      metric1: "Emotional Resonance Intensity",
      metric2: "Project Attachment Depth Index",
      metric3: "User Loyalty Potential Rating",
      metric4: "Trust, Transparency, and Promise Fulfillment",
      guidance: "Consider how users form emotional connections to your project. Think about project recognition, user community, and personal attachment. How does your project create meaningful relationships?"
    },
    elv: {
      title: "Ethical Legacy Value (ELV)",
      description: "Evaluates transformative impact, long-term industry relevance, and legacy value",
      metric1: "Social Impact Factor",
      metric2: "Transformative Potential",
      metric3: "Industry Narrative Impact",
      guidance: "Consider the lasting impact of your project and its ethical foundations. What values does it represent? How does it positively contribute to society or industry?"
    },
    l: {
      title: "Longevity (L)",
      description: "Measures engagement barriers (lower is better)",
      metric1: "Friction Points Analysis",
      metric2: "Engagement Barrier Count",
      guidance: "Reflect on sustainability and resilience factors. How does your project adapt to change? What ensures its long-term relevance?"
    },
    tb: {
      guidance: "Focus on trust-building elements. What makes stakeholders believe in your project? How do you establish and maintain credibility?"
    },
    default: {
      guidance: "Consider the project-specific characteristics relevant to this question."
    },
    eroi: {
      title: "Emotional Rate of Return (EROI)",
      description: "Our proprietary formula for calculating the emotional return on investment combines quantitative metrics with qualitative analysis.",
      formula: "EROI Formula: EROI = Net Emotional Value × Behavioral Conversion ÷ Friction Coefficient",
      formula_text: "EROI = NEV × BC ÷ FC",
      component1: "Net Emotional Value - Composite score of EA, ELV and other metrics",
      component2: "Measures how emotions convert to tangible actions",
      component3: "Quantifies engagement barriers (lower is better)",
      interpretation: "EROI Interpretation Scale:",
      interpretation1: "<0.8: Needs Improvement - Emotional investment not generating sufficient returns",
      interpretation2: "0.8-1.5: Moderate Return - Positive but with optimization opportunities",
      interpretation3: ">1.5: Strong Return - Excellent emotional ROI with high conversion potential"
    },
    differentiators: {
      title: "BEAMEvaluate's Differentiators",
      description: "What sets BEAM's methodology apart from traditional assessment frameworks:",
      key: "Key Differentiating Factors",
      factor1: {
        title: "Emotion-Centered Analysis",
        description: "Prioritizes emotional connections over traditional metrics"
      },
      factor2: {
        title: "Proprietary Algorithms",
        description: "Custom-developed analytical models trained on extensive industry datasets"
      },
      factor3: {
        title: "Cross-Industry Applicability",
        description: "Adaptive framework applicable across diverse industries"
      },
      factor4: {
        title: "Proprietary Algorithm",
        description: "Clear recommendations for optimizing emotional ROI"
      }
    },
    nev: {
      title: "Net Emotional Value (NEV)",
      description: "A comprehensive 0-50 score measuring emotional and strategic value",
      calculation: "NEV Calculation Framework and Rationale",
      formula: "NEV = (Emotional Attachment × 0.40) + (Ethical Legacy × 0.35) + (Longevity × 0.25)",
      explanation: "This scientifically weighted formula provides a nuanced assessment of project value, prioritizing emotional connection (40%), ethical sustainability (35%), and long-term viability (25%)."
    },
    risk: {
      high: {
        label: "High Risk",
        points: "<60 points"
      },
      competitive: {
        label: "Competitive",
        points: "60-80 points"
      },
      exceptional: {
        label: "Exceptional",
        points: ">80 points"
      }
    },
    beamScore: {
      title: "Understanding the BEAM Score",
      overview: "The BEAM Score is a proprietary, multi-dimensional performance metric that transforms complex project dynamics into an intuitive 0-100 scale.",
      components: [
        "Emotional Intelligence: Measures depth of user connection and community engagement",
        "Ethical Alignment: Evaluates project's long-term societal and industry impact",
        "Innovation Potential: Assesses adaptability and transformative capacity",
        "Stakeholder Resonance: Gauges trust, credibility, and perceived value"
      ],
      interpretationScale: {
        low: "0-30: Significant improvement needed. Fundamental restructuring recommended.",
        medium: "40-70: Competitive performance with clear growth opportunities.",
        high: "80-100: Exceptional strategic positioning with strong emotional and innovative leadership."
      }
    }
  },
  industry: {
    title: "Select Your Industry",
    pageTitle: "Project Information",
    pageSubtitle: "Enter details to commence NEV/EROI Assessment",
    detailsTitle: "Project Details",
    projectName: "Project or Business Name",
    projectNamePlaceholder: "Enter the name of your project or business",
    continue: "Continue to Survey",
    assessmentInfo: "The BEAMEvaluate Lite assessment consists of 19 questions across three dimensions.",
    selectRequired: "Please select an industry to continue",
    nameRequired: "Please enter a project name to continue",
    technology: "Technology",
    finance: "Finance",
    healthcare: "Healthcare",
    education: "Education",
    entertainment: "Entertainment",
    retail: "Retail",
    manufacturing: "Manufacturing",
    energy: "Energy",
    hospitality: "Hospitality",
    food_beverages: "Food & Beverages",
    web_30fintech: "Web 3.0 & Fintech",
    other: "Other"
  },
  chart: {
    sensitivityAnalysis: "BEAM's Sensitivity Analysis",
    sentimentAnalysis: "Sentiment Analysis",
    frictionAlert: "Friction Alert",
    dominantEmotions: "Detected Dominant Emotions",
    needsImprovement: "Needs Improvement",
    moderateReturn: "Moderate Return",
    strongReturn: "Strong Return",
    showBeamAI: "Show BEAMEvaluate AI Analysis",
    hideBeamAI: "Hide BEAMEvaluate AI Analysis",
    projectSensitivity: "BEAM's Sensitivity Analysis"
  },
  step: {
    1: "Step 1",
    2: "Step 2",
    3: "Step 3",
  },
  auth: {
    title: "Welcome to BEAMEvaluate",
    subtitle: "Assess Your Project's Emotional Value",
    login: "Sign In",
    loginDesc: "Sign in to continue your assessment",
    loginButton: "Sign In",
    register: "Create Account",
    registerDesc: "Create a new account to start your assessment",
    registerButton: "Create Account",
    email: "Email Address",
    password: "Password",
    fullName: "Full Name",
    back: "Back to Home",
    required: "Authentication Required",
    requiredDesc: "Please sign in to access this page"
  },
  ai: {
    insights: {
      title: "AI Insights",
      description: "AI-powered analysis of your project's emotional and strategic value",
      beta: "Beta",
      noData: "No score data available. Complete the assessment to view AI insights."
    },
    sentiment: {
      analysis: "Sentiment Analysis",
      score: "Sentiment Score",
      positive: "Strong positive sentiment detected in responses",
      neutral: "Neutral to positive sentiment in responses",
      negative: "Areas of concern found in sentiment analysis",
      emotions: "Detected Emotions",
      keywords: "Key Themes",
      distribution: "Sentiment Distribution",
      confidence: "AI Confidence",
      high: {
        confidence: "High confidence analysis based on detailed responses"
      },
      moderate: {
        confidence: "Moderate confidence - consider providing more detail for better accuracy"
      },
      low: {
        confidence: "Low confidence - limited response data available for analysis"
      }
    },
    recommendations: {
      personalized: "Industry-Specific Recommendations",
      impact: "Expected Impact",
      high: "High",
      medium: "Medium",
      moderate: "Moderate",
      insight: "Industry Insight",
      based: {
        on: {
          industry: "These recommendations are optimized for your industry based on current market trends and your assessment results."
        }
      }
    },
    prediction: {
      future: "Performance Prediction",
      current: {
        nev: "Current NEV"
      },
      potential: {
        nev: "Potential Future NEV"
      },
      points: "points",
      timeline: "Implementation Timeline",
      focus: {
        areas: "Focus Areas"
      },
      note: "Prediction Note",
      based: {
        on: "This prediction is based on statistical analysis of similar organizations that implemented targeted improvements in recommended areas."
      }
    },
    competitive: {
      analysis: "Competitive Analysis",
      position: "Market Position",
      based: {
        on: {
          industry: "Based on your NEV score compared to industry averages."
        }
      },
      strengths: "Competitive Strengths",
      weaknesses: "Areas for Improvement",
      opportunities: "Strategic Opportunities"
    }
  },
  dashboard: {
    title: "Your Dashboard",
    subtitle: "View and manage your saved project assessments",
    savedAssessments: "Saved Assessments",
    newAssessment: "New Assessment",
    noSavedAssessments: "You don't have any saved assessments yet.",
    startFirst: "Start Your First Assessment",
    date: "Date",
    delete: "Delete",
    view: "View",
    confirmDelete: "Are you sure you want to delete this assessment?",
    deleted: "Assessment Deleted",
    deletedDesc: "The assessment has been successfully deleted.",
    deleteError: "Delete Error",
    deleteErrorDesc: "There was an error deleting the assessment.",
    loadError: "Load Error",
    loadErrorDesc: "There was an error loading your assessments."
  }
};

export default en;
